import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import {
  Robot,
  House,
  ChatCircle,
  Gear,
  SignOut,
  Plus,
  Lightning,
  Users,
  CreditCard,
  ChartLineUp,
  List,
  X,
  WhatsappLogo,
  CaretRight,
  Bell,
  Check,
  Warning,
  Info,
  CheckCircle,
  CaretDown,
  ArrowRight,
  ShieldCheck,
  AddressBook
} from '@phosphor-icons/react';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingData, setOnboardingData] = useState(null);
  const [inboxUnread, setInboxUnread] = useState(0);
  const { token } = useAuth();
  const wsRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchNotifications();
    checkOnboarding();
    fetchInboxUnread();
  }, []);

  // Refresh inbox unread when navigating back from inbox
  useEffect(() => {
    if (!location.pathname.includes('/inbox')) {
      fetchInboxUnread();
    } else {
      // User entered inbox - they'll read messages, reset badge after a moment
      setTimeout(() => setInboxUnread(0), 1000);
    }
  }, [location.pathname]);

  // WebSocket for real-time badge updates
  useEffect(() => {
    if (!user?.company_id || !token) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const wsUrl = backendUrl.replace('http', 'ws') + `/ws/${user.company_id}?token=${token}`;

    let ws;
    let reconnectTimeout;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_message' && data.message?.sender_type === 'contact') {
            // Only increment if not currently on the inbox page
            if (!location.pathname.includes('/inbox')) {
              setInboxUnread(prev => prev + 1);
            }
          }
        } catch {}
      };

      ws.onclose = () => {
        reconnectTimeout = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      clearTimeout(reconnectTimeout);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [user?.company_id, token]);

  const fetchInboxUnread = async () => {
    try {
      const res = await api.getConversations();
      const total = (res.data || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setInboxUnread(total);
    } catch {}
  };

  const fetchData = async () => {
    try {
      const [statsRes, companyRes] = await Promise.all([
        api.getDashboardStats(),
        api.getCompany()
      ]);
      setStats(statsRes.data);
      setCompany(companyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.getNotifications(20);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const checkOnboarding = async () => {
    try {
      const res = await api.getOnboardingStatus();
      setOnboardingData(res.data);
      if (!res.data.onboarding_completed) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Error checking onboarding:', error);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      fetchNotifications();
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCloseOnboarding = async () => {
    try {
      await api.completeOnboarding();
      setShowOnboarding(false);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isAgent = user?.company_role === 'agent';
  const navItems = isAgent
    ? [
        // Team agents only attend conversations
        { path: '/dashboard', icon: House, label: 'Inicio', exact: true },
        { path: '/dashboard/inbox', icon: ChatCircle, label: 'Inbox' },
        { path: '/dashboard/contacts', icon: AddressBook, label: 'Contactos' },
      ]
    : [
        { path: '/dashboard', icon: House, label: 'Inicio', exact: true },
        { path: '/dashboard/agents', icon: Robot, label: 'Agentes' },
        { path: '/dashboard/inbox', icon: ChatCircle, label: 'Inbox' },
        { path: '/dashboard/contacts', icon: AddressBook, label: 'Contactos' },
        { path: '/dashboard/channels', icon: WhatsappLogo, label: 'Canales' },
        { path: '/dashboard/analytics', icon: ChartLineUp, label: 'Analíticas' },
        { path: '/dashboard/team', icon: Users, label: 'Equipo' },
        { path: '/dashboard/billing', icon: CreditCard, label: 'Facturación' },
        { path: '/dashboard/settings', icon: Gear, label: 'Configuración' },
        ...(user?.role === 'admin' ? [{ path: '/admin', icon: ShieldCheck, label: 'Admin Panel' }] : []),
      ];

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'error': return <Warning size={16} className="text-red-500" />;
      case 'warning': return <Warning size={16} className="text-yellow-500" />;
      case 'success': return <CheckCircle size={16} className="text-green-500" />;
      default: return <Info size={16} className="text-blue-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white/80 backdrop-blur-xl border-b border-[#E4E4E7]/60 z-40 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <List size={24} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/images/novagente-logo.png" alt="Novagente" className="h-6" />
        </div>
        {/* Notification Bell Mobile */}
        <button 
          onClick={() => setShowNotifications(!showNotifications)}
          className="relative p-2"
        >
          <Bell size={24} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Desktop Top Bar with Notifications */}
      <div className="hidden lg:flex fixed top-0 left-64 right-0 h-14 bg-white/80 backdrop-blur-xl border-b border-[#E4E4E7]/60 z-30 items-center justify-end px-6">
        <div className="relative">
          <button 
            onClick={() => setShowNotifications(!showNotifications)}
            data-testid="notification-bell"
            className="relative p-2 hover:bg-[#F4F4F5] rounded-lg transition-colors"
          >
            <Bell size={22} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          
          {/* Notifications Dropdown */}
          {showNotifications && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl border border-[#E4E4E7] shadow-xl z-50 overflow-hidden">
                <div className="p-3 border-b border-[#E4E4E7] flex items-center justify-between">
                  <h3 className="font-bold text-sm">Notificaciones</h3>
                  {unreadCount > 0 && (
                    <button 
                      onClick={handleMarkAllRead}
                      className="text-xs text-[#0047FF] hover:underline"
                    >
                      Marcar todas como leídas
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="p-4 text-center text-[#52525B] text-sm">No hay notificaciones</p>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <div 
                        key={notif.id}
                        className={`p-3 border-b border-[#E4E4E7] last:border-b-0 hover:bg-[#F4F4F5] cursor-pointer ${
                          !notif.is_read ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          handleMarkRead(notif.id);
                          if (notif.metadata?.conversation_id) {
                            navigate(`/dashboard/inbox`);
                            setShowNotifications(false);
                          }
                        }}
                      >
                        <div className="flex items-start gap-2">
                          {getNotificationIcon(notif.type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#09090B] truncate">{notif.title}</p>
                            <p className="text-xs text-[#52525B] line-clamp-2">{notif.message}</p>
                            <p className="text-xs text-[#A1A1AA] mt-1">
                              {new Date(notif.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                            </p>
                          </div>
                          {!notif.is_read && (
                            <div className="w-2 h-2 bg-[#0047FF] rounded-full flex-shrink-0 mt-1" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <Link 
                  to="/dashboard/settings"
                  onClick={() => setShowNotifications(false)}
                  className="block p-3 text-center text-sm text-[#0047FF] hover:bg-[#F4F4F5] border-t border-[#E4E4E7]"
                >
                  Ver historial de errores
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E4E4E7] z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-[#E4E4E7]">
            <div className="flex items-center gap-2">
              <img src="/images/novagente-logo.png" alt="Novagente" className="h-7" />
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X size={20} />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-4 px-3 overflow-y-auto space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-colors ${
                  isActive(item)
                    ? 'bg-[#0047FF]/10 text-[#0047FF] font-semibold'
                    : 'text-[#52525B] hover:bg-[#F4F4F5] hover:text-[#09090B]'
                }`}
              >
                <item.icon size={20} weight={isActive(item) ? 'fill' : 'regular'} />
                <span className="flex-1">{item.label}</span>
                {item.label === 'Inbox' && inboxUnread > 0 && (
                  <span className="w-5 h-5 bg-red-500 text-white text-xs flex items-center justify-center rounded-full font-bold">
                    {inboxUnread > 9 ? '9+' : inboxUnread}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User */}
          <div className="border-t border-[#E4E4E7] p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0047FF] text-white rounded-xl flex items-center justify-center font-bold">
                {user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#09090B] truncate">{company?.name || 'Mi Empresa'}</p>
                <p className="text-xs text-[#52525B] truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-sm text-[#52525B] hover:text-red-500 transition-colors"
            >
              <SignOut size={18} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-14 lg:pt-14">
        <Outlet context={{ stats, company, refreshData: fetchData, onboardingData }} />
      </main>

      {/* Onboarding Modal */}
      {showOnboarding && onboardingData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="p-6 border-b border-[#E4E4E7] flex items-center justify-between">
              <div>
                <h2 className="font-bold text-xl text-[#09090B]">¡Bienvenido a Novagente!</h2>
                <p className="text-sm text-[#52525B]">Comienza en 3 simples pasos</p>
              </div>
              <button 
                onClick={handleCloseOnboarding}
                className="text-[#A1A1AA] hover:text-[#09090B]"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Credit Banner */}
              <div className="bg-green-50 border border-green-200 p-4 mb-6 flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Lightning size={24} weight="fill" className="text-green-600" />
                </div>
                <div>
                  <p className="font-bold text-green-700">¡Tienes ${onboardingData.balance_mxn || 50} MXN de crédito!</p>
                  <p className="text-sm text-green-600">Úsalos para probar todas las funciones</p>
                </div>
              </div>

              {/* Steps */}
              <div className="space-y-4">
                {onboardingData.steps?.map((step, index) => (
                  <div 
                    key={step.id}
                    className={`flex items-start gap-4 p-4 border rounded-xl transition-colors ${
                      step.completed
                        ? 'border-green-200 bg-green-50'
                        : 'border-[#E4E4E7] hover:border-[#0047FF] cursor-pointer'
                    }`}
                    onClick={() => {
                      if (!step.completed) {
                        navigate(step.action_url);
                        setShowOnboarding(false);
                      }
                    }}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      step.completed ? 'bg-green-500' : 'bg-[#0047FF]'
                    }`}>
                      {step.completed ? (
                        <Check size={16} className="text-white" weight="bold" />
                      ) : (
                        <span className="text-white font-bold text-sm">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${step.completed ? 'text-green-700' : 'text-[#09090B]'}`}>
                        {step.title}
                      </p>
                      <p className={`text-sm ${step.completed ? 'text-green-600' : 'text-[#52525B]'}`}>
                        {step.description}
                      </p>
                    </div>
                    {!step.completed && (
                      <ArrowRight size={20} className="text-[#0047FF] flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 border-t border-[#E4E4E7] flex justify-between">
              <button
                onClick={handleCloseOnboarding}
                className="text-[#52525B] hover:text-[#09090B]"
              >
                Lo haré después
              </button>
              <button
                onClick={() => {
                  const nextStep = onboardingData.steps?.find(s => !s.completed);
                  if (nextStep) {
                    navigate(nextStep.action_url);
                  }
                  setShowOnboarding(false);
                }}
                className="px-6 py-2 bg-[#0047FF] text-white rounded-xl font-medium hover:bg-[#0035CC]"
              >
                Comenzar ahora
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Dashboard Home
export function DashboardHome() {
  const { stats, company } = useOutletContext();
  const navigate = useNavigate();

  const botMsgPercentage = company ? Math.min((company.token_usage / company.token_limit) * 100, 100) : 0;

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="font-black text-2xl lg:text-3xl tracking-tighter text-[#09090B]">
          Bienvenido, {company?.name}
        </h1>
        <p className="text-[#52525B]">
          Plan: <span className="font-medium capitalize">{company?.plan || 'Free'}</span>
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <button
          onClick={() => navigate('/dashboard/agents/new')}
          data-testid="quick-create-agent"
          className="flex items-center gap-4 p-6 bg-[#0047FF] text-white rounded-2xl shadow-lg shadow-[#0047FF]/25 hover:bg-[#0035CC] hover:shadow-[#0047FF]/35 transition-all"
        >
          <Plus size={32} />
          <div className="text-left">
            <p className="font-bold text-lg">Crear nuevo agente</p>
            <p className="text-white/70 text-sm">Usa el wizard conversacional</p>
          </div>
          <CaretRight size={24} className="ml-auto" />
        </button>
        <button
          onClick={() => navigate('/dashboard/inbox')}
          data-testid="quick-inbox"
          className="flex items-center gap-4 p-6 bg-white rounded-2xl shadow-sm border border-[#E4E4E7] hover:border-[#0047FF] transition-colors"
        >
          <ChatCircle size={32} className="text-[#0047FF]" />
          <div className="text-left">
            <p className="font-bold text-lg text-[#09090B]">Ver conversaciones</p>
            <p className="text-[#52525B] text-sm">Monitorea tus chats</p>
          </div>
          <CaretRight size={24} className="ml-auto text-[#A1A1AA]" />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Agentes', value: stats?.agents_count || 0 },
          { label: 'Conversaciones', value: stats?.conversations_count || 0 },
          { label: 'Mensajes', value: stats?.messages_count || 0 },
          { label: 'Plan', value: stats?.plan || 'Free', capitalize: true },
        ].map((s) => (
          <div key={s.label} className="p-6 bg-white rounded-2xl shadow-sm border border-[#E4E4E7] hover:shadow-md transition-shadow">
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#52525B] mb-2">{s.label}</p>
            <p className={`font-black text-3xl text-[#09090B] ${s.capitalize ? 'capitalize' : ''}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Bot Messages Usage */}
      <div className="mt-6 p-6 bg-white rounded-2xl shadow-sm border border-[#E4E4E7]">
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="font-mono text-xs tracking-[0.1em] uppercase text-[#52525B]">Mensajes del bot</p>
            <p className="text-2xl font-bold text-[#09090B]">
              {company?.token_usage || 0} / {company?.token_limit || 100}
            </p>
          </div>
          <Link
            to="/dashboard/billing"
            className="text-sm text-[#0047FF] hover:underline"
          >
            Actualizar plan
          </Link>
        </div>
        <div className="h-2.5 bg-[#E4E4E7] rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${botMsgPercentage > 80 ? 'bg-red-500' : 'bg-[#0047FF]'}`}
            style={{ width: `${botMsgPercentage}%` }}
          />
        </div>
      </div>
    </div>
  );
}
